/**
 * 画板相关
 *
 * https://yapi.gaoding.com/project/1536/interface/api/cat_20526
 */
import { pick } from "lodash";
import request from "../utils/request";

/**
 * @param data {{
 *  title: string;
 *  description?: string;
 *  category?: string;
 *  is_private?: '0'|'1'|undefined;
 *  creation?: 'true'|'false'|undefined;
 *  invite_users?: number[];
 * }}
 * @returns {AxiosPromise}
 */
export function createBoard(data) {
  return request({
    method: "post",
    url: "/boards",
    data,
  });
}

/**
 * 获取画板简易信息用于采集、快速采集
 * https://yapi.gaoding.com/project/1536/interface/api/115531
 * @returns {Promise<unknown>}
 */
export function getLastBoards(query = {}) {
  return request({
    url: "/last_boards",
    query: {
      ...query,
      joined: 1,
      fields: "boards:BOARD",
    },
  });
}

/**
 * 获取采集推荐标签
 * https://yapi.gaoding.com/project/1536/interface/api/115793
 * @param board_id
 * @returns {Promise<*>}
 */
export function getBoardSuggestTags({ board_id }) {
  return request({
    url: `/boards/${board_id}/recommend_tags`,
  });
}

/**
 * 获取画板详情
 * https://yapi.gaoding.com/project/1536/interface/api/115593
 * @param {{
 *  board_id: string|number;
 *  ignoreMessage?: boolean;
 *  pins?: 'true'|'false'| number;
 * }}
 * @returns {Promise<*>}
 */
export function getBoard({ board_id, ignoreMessage, pins }) {
  return request({
    url: `/boards/${board_id}`,
    ignoreMessage,
    query: {
      fields: "board:BOARD_DETAIL",
      pins,
    },
  });
}

/**
 * @param data {{
 *  board_id: number;
 *  title?: string;
 *  description?: string;
 *  category?: string;
 *  is_private?: '0'|'1'|undefined;
 * }}
 * @returns {AxiosPromise}
 */
export function updateBoard({ board_id, ...data }) {
  return request({
    method: "put",
    url: `/boards/${board_id}`,
    data,
  });
}

/**
 * @param {number} id  画板id
 * @returns {AxiosPromise}
 */
export function getBoardInfo(id, query) {
  return request({
    method: "get",
    url: `/boards/${id}`,
    query: {
      fields: "board:BOARD_DETAIL",
      ...query,
    },
  });
}
/**
 * 删除画板
 * @param {*} id
 * @returns
 */
export function delBoard(id) {
  return request({
    method: "delete",
    url: `/boards/${id}`,
  });
}
/**
 * 设置封面
 * @param data {{
 *    board_id: number,
 *    pin_id: number
 * }}
 * @returns {AxiosPromise}
 */
export function setCover({ board_id, ...data }) {
  return request({
    method: "post",
    url: `/boards/${board_id}/board-cover`,
    data,
  });
}
/**
 * 画板封面删除-使用默认封面（最新采集）
 * @param {*} param0
 * @returns
 */
export function delCover({ board_id }) {
  return request({
    method: "delete",
    url: `/boards/${board_id}/board-cover`,
  });
}

/**
 * 创建专辑
 * @param {string} title
 */
export function createCollections(title) {
  return request({
    method: "post",
    url: `/collections`,
    data: {
      title,
    },
  });
}
/**
 * 获取专辑信息
 * @returns {Promise<{collections: {
 *  collection_id: number;
 *  title: string;
 *  board_ids: string;
 *  description: string;
 *  user_id: number;
 * }[],
 * err: number,
 * msg: string,
 * }}
 */
export function getCollections(user_id) {
  return request({
    method: "get",
    url: `/users/${user_id}/collections`,
  });
}

/**
 * 删除专辑
 * https://yapi.gaoding.com/project/1536/interface/api/116934
 * @param collection_id
 * @returns {Promise<*|undefined>}
 */
export function deleteCollection({ collection_id }) {
  return request({
    method: "delete",
    url: `/collections/${collection_id}`,
  });
}

/**
 * 修改专辑
 * https://yapi.gaoding.com/project/1536/interface/api/118284
 * @param collection_id
 * @param title
 * @param board_ids
 * @param description
 * @param seq
 * @returns {Promise<any|undefined>}
 */
export function updateCollection({
  collection_id,
  title,
  board_ids,
  description,
  seq,
}) {
  return request({
    method: "put",
    url: `/collections/${collection_id}`,
    data: {
      title,
      board_ids,
      description,
      seq,
    },
  });
}

/**
 * 修改专辑排序
 * @param  {number} currentId 当前操作的专辑id
 * @param {number|undefined} nextToId 前一个专辑的id，为空则设置当前专辑为第一个
 * @returns {Promise<any|undefined>}
 */
export function moveCollectionNextTo({ currentId, nextToId }) {
  return request({
    method: "post",
    url: `/collections/sort`,
    data: {
      collection_id: currentId,
      after: nextToId,
    },
  });
}

/**
 * 喜欢画板
 * @param board_id {number}
 * @returns {AxiosPromise}
 */
export function likeBoard(board_id) {
  return request({
    method: "post",
    url: `/boards/${board_id}/like`,
  });
}

/**
 * 取消喜欢采集
 * @param board_id {number}
 * @returns {AxiosPromise}
 */
export function unlikeBoard(board_id) {
  return request({
    method: "post",
    url: `/boards/${board_id}/unlike`,
  });
}

/**
 * 关注画板
 * @param {number} id 画板id
 * @returns
 */
export async function followBoard(id) {
  return request({
    method: "post",
    url: `/boards/${id}/follow`,
  });
}

/**
 * 取消关注画板
 * @param {number} id 画板id
 * @returns
 */
export async function unfollowBoard(id) {
  return request({
    method: "post",
    url: `/boards/${id}/unfollow`,
  });
}
/**
 * 获取画板下关注者
 * @param {number} board_id 画板id
 * @returns {AxiosPromise}
 */
export async function getBoardFollowers({ board_id, max, limit }) {
  return request({
    url: `/boards/${board_id}/followers`,
    query: {
      max,
      limit,
    },
  });
}

/**
 * 获取画板下采集
 * @returns {Promise<{pins: IPin[]}>}
 */
export async function getBoardPins({ board_id, ...query }) {
  return request({
    url: `/boards/${board_id}/pins`,
    query: {
      ...query,
      fields: "pins:PIN|board:BOARD_DETAIL|check",
    },
  });
}

/**
 * 获取画板推荐采集
 * https://yapi.gaoding.com/project/1536/interface/api/207545
 * @param {{
 *  board_id: number;
 *  limit: number;
 *  page?: number;
 *  pins: { file_id: number }[];
 * }} query
 * @returns {Promise<{pins: IPin[], err: number, msg: string}>}
 */
export async function getBoardRecommendPins({ board_id, ...data }) {
  return request({
    url: `/boards/${board_id}/recommend/pins`,
    data,
    method: "POST",
    ignoreMessage: true,
  });
}

/**
 * 获取用户关注画板
 * @param {number} urlname 用户urlname
 * @returns
 */
/**
 * @param {{
 *   urlname: any;
 *   per_page?: number;
 *   page?: number;
 *   has_collection?: number;
 *   [key: any]: any;
 * }} query
 * @returns {Promise<*|{msg: string, err: number}|undefined>}
 */
export async function getFollowingBoards({
  urlname,
  per_page,
  page,
  has_collection,
  ...rest
}) {
  return request({
    url: `/${urlname}/following/boards`,
    query: {
      per_page,
      page,
      has_collection,
      ...rest,
    },
  });
}

/**
 * 获取用户关注画板,根据用户分组
 * @param {number} urlname 用户urlname
 * @returns
 */
export async function getFollowingUserBoards({ urlname, max, since, ...rest }) {
  return request({
    url: `/${urlname}/following/user_boards`,
    query: {
      max,
      since,
      ...rest,
    },
  });
}

/**
 * 画板排序自定义设置（已经废弃）
 * https://yapi.gaoding.com/project/1536/interface/api/117867
 * @param ids
 * @returns {Promise<any|undefined>}
 */
// export async function sortBoard({ ids }) {
//   return request({
//     method: "post",
//     url: `/boards/sort`,
//     data: {
//       ids: ids.join(","),
//     },
//   });
// }

/**
 * 画板排序
 * https://yapi.gaoding.com/project/1536/interface/api/162755
 * @param board_id
 * @param after
 * @returns {Promise<any|undefined>}
 */
export async function sortBoard({ board_id, after }) {
  return request({
    method: "post",
    url: `/boards/sort2`,
    data: {
      board_id,
      after,
    },
  });
}

/**
 * 专辑内画板排序
 * https://yapi.gaoding.com/project/1536/interface/api/162754
 * @param board_id
 * @param after
 * @param collection_id
 * @returns {Promise<any|undefined>}
 */
export async function sortCollectionBoard({ collection_id, board_id, after }) {
  return request({
    method: "post",
    url: `/collections/${collection_id}/sort`,
    data: {
      board_id,
      after,
    },
  });
}

/**
 * 原创画板排序
 * https://yapi.gaoding.com/project/1536/interface/api/162755
 * @param board_id
 * @param after
 * @returns {Promise<any|undefined>}
 */
export async function sortMuseBoard({ board_id, after }) {
  return request({
    method: "post",
    url: `/muse_boards/sort2`,
    data: {
      board_id,
      after,
    },
  });
}

/**
 * 获取偏好画板
 * @returns {AxiosPromise}
 */
export function getFavoriteBoards({ urlname, limit, max, since }) {
  return request({
    url: `/boards/favorite/${urlname}`,
    query: {
      limit,
      max,
      since,
    },
  });
}
/**
 * 获取推荐画板
 * @returns {AxiosPromise}
 */
export async function getRecommendBoards({ limit, max, since, page }) {
  return request({
    url: "/recommend/hot/boards",
    query: {
      limit,
      max,
      since,
      page,
    },
  });
}
/**
 * 已删除画板列表
 * @returns {AxiosPromise}
 */
export async function getRecycleBoards({ urlname, limit, page }) {
  return request({
    url: `/${urlname}/deleted/boards`,
    query: {
      limit,
      page,
    },
  });
}

export async function recoverBoard({ urlname, board_id }) {
  return request({
    method: "post",
    url: `/${urlname}/recover/board`,
    data: {
      board_id,
    },
  });
}

/**
 * 画板排序
 * @param {number} boardId
 * @param {{
 *  pin_ids: number[],
 *  after?: number,
 *  before?:  number
 * }} data
 * @returns
 */
export async function boardPinSort(boardId, data) {
  return request({
    url: `/boards/${boardId}/sort`,
    method: "POST",
    data,
  });
}

export async function openBoardSort(board) {
  return request({
    url: `/boards/${board.board_id}`,
    method: "PUT",
    data: {
      ...pick(board, "title", "description"),
      seq_order: 1,
    },
  });
}

/**
 *
 * @param {{
 *  board_id: number;
 *  page?: number;
 *  limit?: number;
 * }} query
 * @returns {Promise<{
 *  data: (IUser & {status: 0|1|2|3|4})[];
 *  total: number;
 *  page: number;
 *  limit: number;
 * }>}
 */
export async function getBoardMembers({ board_id, ...query }) {
  return request({
    method: "GET",
    url: `/boards/${board_id}/collab/members`,
    query: {
      page: 1,
      limit: 30,
      ...query,
    },
  });
}

export async function inviteBoardMember({ board_id, user_id }) {
  return request({
    method: "POST",
    url: `/boards/${board_id}/collab/invite/direct`,
    data: {
      invite_user_id: user_id,
    },
  });
}

export async function removeBoardMember({ board_id, user_id }) {
  return request({
    method: "DELETE",
    url: `/boards/${board_id}/collab/members/${user_id}`,
  });
}

export async function exitBoardMember({ board_id }) {
  return request({
    method: "POST",
    url: `/boards/${board_id}/collab/leave`,
  });
}

/**
 * 确认加入协作画板
 * @param {{invite_code: string}} param0
 * @returns {Promise<{err?: number, msg?: string}>}
 */
export async function confirmJoinBoard({ invite_code }) {
  return request({
    method: "POST",
    url: `/collab/invitations/${invite_code}/accept`,
  });
}

/**
 * 获取协作邀请分享链接
 * @param {{board_id: number}} param0
 * @returns {Promise<{share_link: string, invitation_code: string, err?: number, msg?: string}>}
 */
export async function getInviteCode({ board_id }) {
  return request({
    method: "GET",
    url: `/boards/${board_id}/collab/invite/share`,
  });
}

/**
 *
 * @param {*} param0
 * @returns {Promise<{
 *  board: IBoardInfo;
 *  invitation: IBoardInviteInfo;
 *  inviter: IUser;
 *  member_limit: number;
 *  member_total: number;
 *  is_member: boolean | null
 * }>}
 * }
 */
export async function getBoardByInviteCode({ invite_code, cookie }) {
  return request({
    method: "GET",
    url: `/collab/invitations/${invite_code}/info`,
    headers: {
      cookie: cookie,
    },
  });
}

export async function changeCollabRole({ board_id, role }) {
  return request({
    method: "PUT",
    url: `/boards/${board_id}/collab/permissions`,
    data: {
      role,
    },
  });
}

/**
 *
 * @param {{
 *  nickname: string;
 *  page: number;
 * limit: number;
 * board_id: number;
 * }} query
 * @returns
 */
export async function searchBoardMembers(query) {
  return request({
    method: "GET",
    url: `/collab/search-users`,
    query,
  });
}

/**
 * 获取多人画板列表
 * @param {{
 *  urlname: string;
 *  page?: number;
 *  limit?: number;
 * }} query
 * @returns
 */
export async function getCollabBoards({ urlname, ...query }) {
  return request({
    method: "GET",
    url: `/${urlname}/collab_boards`,
    query,
  });
}
